import { LiveMultiValuesState, LiveMultiValuesMessage, LiveMultiValuesEvent, LiveValue, LiveMultiValuesMeta } from './api'

export const liveMultiValuesWidgetReducer: WidgetReducerType<LiveMultiValuesState, LiveMultiValuesMessage> = (
    state = {
        metaByLayer: new Map<number, LiveMultiValuesMeta>(),
        meta: {},
        values: []
    },
    message
) => {
    switch (message?.type) {
        case 'start': {
            // const metaByLayer = { ...state.metaByLayer, [message.layer]: validMeta(message.events.info) }
            const metaByLayer = new Map(state.metaByLayer).set(message.layer, validMeta(message.events.info))

            return {
                metaByLayer,
                meta: mergeMetas(metaByLayer),
                values: mergeValues(state.values, [], message.layer)
            }
        }

        case 'event':
            return {
                metaByLayer: state.metaByLayer,
                meta: state.meta,
                values: mergeValues(state.values, message.events, message.layer)
            }

        default:
            return state
    }
}

/**
 * Return a valid meta removing invalid parameters.
 *
 * @param meta meta parameters received
 */
function validMeta(meta: LiveMultiValuesMeta): LiveMultiValuesMeta {
    return {
        ...meta,
        channels: Array.isArray(meta.channels)
            ? meta.channels
                  .filter(channel => typeof channel.mnemonic === 'string')
                  .map(channel => ({ ...channel, title: channel.title ?? channel.mnemonic })) // add title from mnemonic if not present
            : null
    }
}

/**
 * Merge the meta parameters from all layers, giving priority to values on the first layer (and merging the channels list).
 *
 * @param metaByLayer
 */
function mergeMetas(metaByLayer: Map<number, LiveMultiValuesMeta>): LiveMultiValuesMeta {
    let meta: LiveMultiValuesMeta = {}

    Array.from(metaByLayer.entries())
        .sort(([a], [b]) => b - a)
        .forEach(([, layerMeta]) => {
            meta = { ...meta, ...layerMeta, channels: (layerMeta.channels ?? []).concat(meta.channels ?? []) }
        })

    return meta
}

/**
 * Merge the new values received with the old ones.
 *
 * @param stateValues old values
 * @param events new events
 * @param layer layer that generated the events
 */
function mergeValues(stateValues: LiveValue[], events: LiveMultiValuesEvent[], layer: number): LiveValue[] {
    const distinctEvents = Array.from(
        new Map(
            events.filter(ev => typeof (ev.mnemonic ?? ev.title) === 'string').map(ev => [ev.mnemonic ?? ev.title, ev])
        ).values()
    )

    // return the latest values received per layer
    return [
        ...stateValues.filter(value => value.layer < layer),
        ...distinctEvents.map(ev => ({
            ...ev,
            mnemonic: ev.mnemonic ?? ev.title, // a title-only "simple query" event has no mnemonic of its own -
            // normalize it here too (not just in the dedup key above), so every downstream mnemonic-keyed
            // lookup/key sees a real value instead of undefined
            title: ev.title ?? ev.mnemonic, // add title from mnemonic if not present
            color: ev.color ?? ev.__color, // backwards compatibility
            uom: ev.uom ?? ev.__uom, // backwards compatibility
            layer
        })),
        ...stateValues.filter(value => value.layer > layer)
    ]
}
